import { Button } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { FunctionComponent, ReactElement, useState } from "react";

// Components
import CustomModal from "../../shared/Modals/CustomModal";

// Hooks
import { useAuth } from "../../hooks/useAuth";
import usePermission from "../../hooks/usePermission";
import useClaimsToast from "../../hooks/useToast";

// Protos
import {
	ExportPaymentsToCsvResponse,
	GetApprovedCountForAnalystResponse
} from "../../utils/api/protos/messages/claims.message_pb";

// Utils
import { exportPaymentsToCsv, getApprovedClaimCountForAnalyst } from "../../utils/api/claims";
import { UserRole } from "../../utils/consts";
import { handleDocClick } from "../../utils/helpers";

interface NewPaymentExportButtonProps {
	onExport(): void;
}

/**
 * Handles the Payments export listing page
 * @param props typeof: PaymentsPageProps
 * @returns ReactElement
 */
const NewPaymentExportButton: FunctionComponent<NewPaymentExportButtonProps> = (
	props: NewPaymentExportButtonProps
): ReactElement => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
	const [approvedClaimCount, setApprovedClaimCount] = useState<number>(0);
	const { getUser } = useAuth();
	const { hasPaymentsExportAccess } = usePermission();
	const user = getUser();
	const toast = useClaimsToast();

	const handleExportClaims = async (): Promise<void> => {
		setIsLoading(true);

		await exportPaymentsToCsv()
			.then((response: ExportPaymentsToCsvResponse) => {
				toast("Success", `Your export is processing, please allow a few moments for processing`, "success");
			})
			.catch((err) => {
				console.error((err as RpcError).message);
				toast("Error", (err as RpcError).message, "error");
			})
			.finally(() => {
				setIsLoading(false);
				if (isExportModalOpen) setIsExportModalOpen(false);
				props.onExport();
			});
	};

	const getExportCount = async (): Promise<void> => {
		setIsLoading(true);
		await getApprovedClaimCountForAnalyst()
			.then((response: GetApprovedCountForAnalystResponse) => {
				setApprovedClaimCount(response.getCount());
				setIsExportModalOpen(true);
			})
			.catch((err) => {
				console.error((err as RpcError).message);
				toast("Error", (err as RpcError).message, "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const getExportModal = (): ReactElement => {
		if (approvedClaimCount >= 5000) {
			return (
				<CustomModal
					isOpen={isExportModalOpen}
					title={"New Payments Export"}
					size="lg"
					message="There are too many Claims to Export. Please contact Support."
					onContinue={() => setIsExportModalOpen(false)}
					onCancel={() => setIsExportModalOpen(false)}
					continueText={"Okay"}
				/>
			);
		} else if (approvedClaimCount > 0) {
			return (
				<CustomModal
					isOpen={isExportModalOpen}
					title={"New Payments Export"}
					size="lg"
					message={`${approvedClaimCount} ${approvedClaimCount > 1 ? "Claims are" : "Claim is"} ready to be Queued for Payment. Continue?`}
					onContinue={() => handleExportClaims()}
					onCancel={() => setIsExportModalOpen(false)}
					continueText={"Continue"}
					continueLoading={!!approvedClaimCount && isLoading}
				/>
			);
		} else {
			return (
				<CustomModal
					isOpen={isExportModalOpen}
					title={"New Payments Export"}
					size="lg"
					message="You have no Approved Claims available for Export."
					onContinue={() => setIsExportModalOpen(false)}
					onCancel={() => setIsExportModalOpen(false)}
					continueText={"Okay"}
				/>
			);
		}
	};

	const canUserExport = (): void => {
		const allowedRoles: Array<UserRole> = [UserRole.ClaimsAdmin, UserRole.Payments];
		if (user?.role.split(",").some((role: string) => allowedRoles.includes(role as UserRole))) {
			getExportCount();
		} else {
			toast("Access Denied", "You do not have the correct permission to create a payments export", "warning");
		}
	};

	return (
		<>
			{isExportModalOpen && getExportModal()}
			<Button
				marginRight={4}
				size="sm"
				onClick={() => canUserExport()}
				isDisabled={!hasPaymentsExportAccess}
				colorScheme="orange"
				padding={2}>
				New Payments Export
			</Button>
		</>
	);
};

export default NewPaymentExportButton;
